import React, { useState, useEffect, useMemo, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { datasetsAPI } from "../../utils/datasetsAPI";
import { showToast } from "../../components/Toast";
import { showConfirm } from "../../components/ConfirmModal";
import {
  FiEdit2,
  FiTrash2,
  FiSave,
  FiX,
  FiEye,
  FiEdit,
  FiPlus,
  FiRefreshCw,
  FiDownload,
  FiUpload,
  FiLock,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";
import "./DataEntry.css";

// Keys auto-populated by SalesService — must not be added / overwritten manually
const SALES_RECORDS_SYSTEM_KEYS = new Set([
  "invoice_number", "product_name", "quantity", "unit_price",
  "vat_rate", "total_amount", "payment_method", "customer_name", "created_at",
]);

const isSalesRecords = (dataset) => dataset?.name === "sales_records";

function DataEntry() {
  const { user } = useAuth();
  const [activeDataset, setActiveDataset] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showAddDatasetModal, setShowAddDatasetModal] = useState(false);
  const [newDatasetName, setNewDatasetName] = useState("");
  const [newDatasetLabel, setNewDatasetLabel] = useState("");
  const [loadingRows, setLoadingRows] = useState(false);
  const [editingRowIds, setEditingRowIds] = useState(new Set());
  const [viewMode, setViewMode] = useState("view"); // "entry" or "view"
  const [showExportPanel, setShowExportPanel] = useState(false);

  const [columns, setColumns] = useState([
    { id: 1, key: "date", label: "Date", type: "date" },
    { id: 2, key: "product", label: "Product", type: "text" },
    { id: 3, key: "quantity", label: "Quantity", type: "number" },
    { id: 4, key: "price", label: "Price", type: "number" },
  ]);

  const [rows, setRows] = useState([]);
  const [existingRows, setExistingRows] = useState([]); // Rows from database
  const [rowsPagination, setRowsPagination] = useState(null);
  const [currentRowsPage, setCurrentRowsPage] = useState(1);
  const [rowsPageSize, setRowsPageSize] = useState(8);
  const [showAddColumnModal, setShowAddColumnModal] = useState(false);
  const [newColumnKey, setNewColumnKey] = useState("");
  const [newColumnLabel, setNewColumnLabel] = useState("");
  const [newColumnType, setNewColumnType] = useState("text");
  const [addingColumn, setAddingColumn] = useState(false);
  const [exportDateFrom, setExportDateFrom] = useState("");
  const [exportDateTo, setExportDateTo] = useState("");
  const [exportDatasetId, setExportDatasetId] = useState("");
  const [exporting, setExporting] = useState(false);
  const [uploadingBulk, setUploadingBulk] = useState(false);
  const bulkInputRef = useRef(null);
  const { t } = useTranslation();

  const organizationId = useMemo(() => {
    if (user?.organization && typeof user.organization === "object") {
      return user.organization.id;
    }
    return user?.organization || null;
  }, [user]);

  useEffect(() => {
    if (organizationId) {
      loadDatasets();
    }
  }, [organizationId]);

  const loadDatasets = async () => {
    try {
      setLoading(true);
      const response = await datasetsAPI.list(organizationId);
      const datasetsList = response?.data || response || [];
      setDatasets(datasetsList);

      // Set first dataset as active if available
      if (datasetsList.length > 0 && !activeDataset) {
        selectDataset(datasetsList[0]);
      }
    } catch (error) {
      console.error("Failed to load datasets:", error);
      showToast.error(t("dataEntry.loadFailed", "Failed to load datasets"));
    } finally {
      setLoading(false);
    }
  };

  const selectDataset = async (dataset) => {
    setActiveDataset(dataset);
    setViewMode("view");
    setEditingRowIds(new Set());

    // Reset pagination
    setCurrentRowsPage(1);
    setRowsPagination(null);

    // Set columns from dataset metadata
    if (dataset.columns && dataset.columns.length > 0) {
      setColumns(
        dataset.columns.map((col, idx) => ({
          id: col.id || idx + 1,
          key: col.key,
          label: col.label,
          type: col.column_type || col.type || "text",
          required: col.required === true,
        })),
      );
    } else {
      // If no columns, show warning
      showToast.warning(
        "This dataset has no columns defined. Please create a new dataset with columns.",
      );
      setColumns([]);
    }

    // Reset new entry rows
    setRows([]);

    // Load existing rows from database (page 1)
    await loadExistingRows(dataset.id, 1);
  };

  const loadExistingRows = async (
    datasetId,
    page = currentRowsPage,
    pageSize = rowsPageSize,
  ) => {
    if (!datasetId || !organizationId) return;

    try {
      setLoadingRows(true);
      const response = await datasetsAPI.listRows(organizationId, datasetId, {
        page: page,
        page_size: pageSize,
      });
      console.log("Load existing rows response:", response); // Debug log

      // Handle paginated response format
      if (response?.success && response?.data) {
        setExistingRows(response.data);
        setRowsPagination(response.pagination || null);
      } else {
        // Handle different response formats (fallback)
        const rowsData = Array.isArray(response)
          ? response
          : response?.data || [];

        console.log("Processed rows data (non-paginated):", rowsData); // Debug log
        setExistingRows(rowsData);
        setRowsPagination(null);
      }
    } catch (error) {
      console.error("Failed to load existing rows:", error);
      showToast.error(
        t("dataEntry.loadFailed", "Failed to load existing data"),
      );
      setExistingRows([]);
      setRowsPagination(null);
    } finally {
      setLoadingRows(false);
    }
  };

  const createNewDataset = async () => {
    if (!newDatasetName.trim()) {
      showToast.warning(
        t("dataEntry.datasetNameError", "Please enter a dataset name"),
      );
      return;
    }

    if (columns.length === 0) {
      showToast.warning(
        t("dataEntry.columnsRequired", "Please add at least one column"),
      );
      return;
    }

    try {
      const payload = {
        name: newDatasetName.toLowerCase().replace(/\s+/g, "_"),
        label: newDatasetLabel || newDatasetName,
        columns: columns.map((col) => ({
          key: col.key,
          label: col.label,
          type: col.type, // Backend expects 'type' not 'column_type'
          required: false,
        })),
      };

      const response = await datasetsAPI.createWithColumns(
        organizationId,
        payload,
      );
      const newDataset = response?.data || response;

      // Validate that columns were created properly
      if (!newDataset.columns || newDataset.columns.length === 0) {
        showToast.error(
          "Dataset created but columns were not saved. Please try again.",
        );
        return;
      }

      showToast.success(
        `Dataset created with ${newDataset.columns.length} columns`,
      );

      // Reload datasets to get fresh data
      await loadDatasets();

      // Select the new dataset
      const updatedDataset = await datasetsAPI.list(organizationId);
      const created = (updatedDataset?.data || updatedDataset).find(
        (d) => d.name === newDataset.name,
      );
      if (created) {
        selectDataset(created);
      }

      setShowAddDatasetModal(false);
      setNewDatasetName("");
      setNewDatasetLabel("");
    } catch (error) {
      console.error("Failed to create dataset:", error);
      showToast.error(
        t("dataEntry.datasetCreateFailed", "Failed to create dataset"),
      );
    }
  };

  const addRow = () => {
    if (columns.length === 0) {
      showToast.warning(
        "This dataset has no columns. Please select a different dataset.",
      );
      return;
    }

    const newRow = { id: Date.now() };
    columns.forEach((col) => {
      newRow[col.key] = "";
    });
    setRows([...rows, newRow]);
  };

  const updateCell = (rowId, colKey, value) => {
    setRows(
      rows.map((row) => (row.id === rowId ? { ...row, [colKey]: value } : row)),
    );
  };

  const deleteRow = (rowId) => {
    setRows(rows.filter((row) => row.id !== rowId));
  };

  const submitData = async () => {
    if (!activeDataset) {
      showToast.warning("Please select a dataset first");
      return;
    }

    if (columns.length === 0) {
      showToast.warning("This dataset has no columns defined");
      return;
    }

    if (rows.length === 0) {
      showToast.warning("Please add at least one row of data");
      return;
    }

    // Validate that all rows have data
    const emptyRows = rows.filter((row) => {
      return columns.every(
        (col) => !row[col.key] || row[col.key].trim() === "",
      );
    });

    if (emptyRows.length > 0) {
      showToast.warning(
        "Some rows are completely empty. Please fill in data or remove them.",
      );
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        dataset: activeDataset.name, // Backend expects dataset NAME (string)
        rows: rows.map((row) => {
          const { id, ...data } = row;
          // Only include columns that exist in the dataset
          const cleanData = {};
          columns.forEach((col) => {
            cleanData[col.key] = data[col.key] || "";
          });
          return cleanData;
        }),
      };

      console.log("Submitting data:", payload); // Debug log

      const response = await datasetsAPI.bulkRows(organizationId, payload);

      const successCount = response?.data?.created_count || rows.length;
      showToast.success(`${successCount} row(s) submitted successfully!`);
      setRows([]); // Clear rows after successful submission

      // Reload existing rows to show the newly submitted data
      await loadExistingRows(activeDataset.id);
    } catch (error) {
      console.error("Failed to submit data:", error);
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to submit data";
      showToast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const updateExistingRow = async (rowId, rowData) => {
    if (!activeDataset) return;

    try {
      const { id, created_at, dataset, ...data } = rowData;

      await datasetsAPI.updateRow(organizationId, activeDataset.id, rowId, {
        data: data,
      });

      showToast.success(t("dataEntry.saveRow", "Row saved"));

      // Remove from editing set
      const newEditing = new Set(editingRowIds);
      newEditing.delete(rowId);
      setEditingRowIds(newEditing);

      // Reload rows
      await loadExistingRows(activeDataset.id);
    } catch (error) {
      console.error("Failed to update row:", error);
      showToast.error(t("dataEntry.deleteRowFailed", "Failed to save row"));
    }
  };

  const deleteExistingRow = async (rowId) => {
    if (!activeDataset) return;

    const confirmed = await showConfirm({
      title: "Delete row",
      message: "Are you sure you want to delete this row?",
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "danger",
    });
    if (!confirmed) return;

    try {
      await datasetsAPI.deleteRow(organizationId, activeDataset.id, rowId);
      showToast.success(t("dataEntry.deleteRowSuccess", "Row deleted"));

      // Reload rows
      await loadExistingRows(activeDataset.id);
    } catch (error) {
      console.error("Failed to delete row:", error);
      showToast.error(t("dataEntry.deleteRowFailed", "Failed to delete row"));
    }
  };

  const handleExportCsv = async () => {
    if (!organizationId) return;
    const datasetId = exportDatasetId || activeDataset?.id;
    if (!datasetId) {
      showToast.warning("Select a dataset to export");
      return;
    }
    if (!exportDateFrom || !exportDateTo) {
      showToast.warning("Select date range (from and to)");
      return;
    }
    try {
      setExporting(true);
      const blob = await datasetsAPI.exportCsv(organizationId, {
        date_from: exportDateFrom,
        date_to: exportDateTo,
        dataset_id: Number(datasetId),
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `data_export_${exportDateFrom}_${exportDateTo}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      showToast.success(t("dataEntry.templateDownloaded", "Export downloaded"));
    } catch (err) {
      showToast.error(err?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const downloadTemplate = () => {
    if (!activeDataset || columns.length === 0) {
      showToast.warning("Select a dataset with columns first");
      return;
    }
    const header = columns.map((c) => c.key).join(",");
    const csv = header + "\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${activeDataset.name || "template"}_template.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast.success(t("dataEntry.templateDownloaded", "Template downloaded"));
  };

  const parseCsvLine = (line) => {
    const result = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        inQuotes = !inQuotes;
      } else if (inQuotes) {
        current += ch;
      } else if (ch === ",") {
        result.push(current.trim());
        current = "";
      } else {
        current += ch;
      }
    }
    result.push(current.trim());
    return result;
  };

  /** Validate a single cell: type and required. Returns error string or null. */
  const validateCell = (value, col) => {
    const raw = value === "" || value == null ? "" : String(value).trim();
    const isEmpty = raw === "";

    if (col.required && isEmpty) {
      return `${col.label || col.key}: required`;
    }
    if (isEmpty) return null;

    const t = (col.type || "text").toLowerCase();
    if (t === "number") {
      const n = parseFloat(String(raw).replace(/,/g, ""));
      if (isNaN(n)) return `${col.label || col.key}: must be a number`;
      return null;
    }
    if (t === "date") {
      const s = String(raw).slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        return `${col.label || col.key}: use YYYY-MM-DD format`;
      }
      return null;
    }
    return null;
  };

  const handleBulkUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !activeDataset || columns.length === 0) return;
    if (!organizationId) {
      showToast.warning("No organization assigned");
      return;
    }
    try {
      setUploadingBulk(true);
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length < 2) {
        showToast.warning(
          "CSV must have a header row and at least one data row",
        );
        return;
      }
      const headerKeys = parseCsvLine(lines[0]).map((k) => String(k).trim());
      const colByKey = {};
      columns.forEach((c) => {
        colByKey[c.key] = c;
      });
      const orderedKeys = columns.map((c) => c.key);

      // Check header: all dataset columns must be present
      const missingCols = orderedKeys.filter((k) => !headerKeys.includes(k));
      if (missingCols.length > 0) {
        showToast.error(
          `CSV is missing column(s): ${missingCols.join(", ")}. Use the downloaded template.`,
        );
        return;
      }

      const allErrors = [];
      const rows = [];

      for (let i = 1; i < lines.length; i++) {
        const values = parseCsvLine(lines[i]);
        const row = {};
        const rowErrors = [];

        for (let j = 0; j < headerKeys.length; j++) {
          const key = headerKeys[j];
          const col = colByKey[key];
          if (!col) continue;
          const val = values[j] ?? "";
          const err = validateCell(val, col);
          if (err) rowErrors.push(err);
          row[col.key] = val === "" ? "" : String(val).trim();
        }

        orderedKeys.forEach((k) => {
          if (!(k in row)) row[k] = "";
        });

        if (rowErrors.length > 0) {
          allErrors.push(`Row ${i + 1}: ${rowErrors.join("; ")}`);
        } else {
          const hasAny = orderedKeys.some((k) => row[k] !== "");
          if (hasAny) rows.push(row);
        }
      }

      if (allErrors.length > 0) {
        const maxShow = 3;
        const summary =
          allErrors.length > maxShow
            ? `${allErrors.slice(0, maxShow).join(" — ")} … and ${allErrors.length - maxShow} more row(s).`
            : allErrors.join(" — ");
        showToast.error(`Validation failed: ${summary}`);
        return;
      }

      if (rows.length === 0) {
        showToast.warning("No valid data rows found in CSV");
        return;
      }

      const payload = { dataset: activeDataset.name, rows };
      const res = await datasetsAPI.bulkRows(organizationId, payload);
      const count = res?.data?.length ?? rows.length;
      showToast.success(`${count} row(s) uploaded successfully`);
      setRows([]);
      if (bulkInputRef.current) bulkInputRef.current.value = "";
      await loadExistingRows(activeDataset.id);
    } catch (err) {
      const msg = err?.response?.data?.details?.validation_errors
        ? err.response.data.details.validation_errors
            .map((e) => `Row ${e.row_index}: ${(e.errors || []).join(", ")}`)
            .join("; ")
        : err?.message || "Upload failed";
      showToast.error(msg);
    } finally {
      setUploadingBulk(false);
    }
  };

  const toggleEditMode = (rowId) => {
    const newEditing = new Set(editingRowIds);
    if (newEditing.has(rowId)) {
      newEditing.delete(rowId);
    } else {
      newEditing.add(rowId);
    }
    setEditingRowIds(newEditing);
  };

  const updateExistingCell = (rowId, colKey, value) => {
    setExistingRows(
      existingRows.map((row) =>
        row.id === rowId
          ? { ...row, data: { ...row.data, [colKey]: value } }
          : row,
      ),
    );
  };

  const handleAddColumn = async () => {
    if (!activeDataset) {
      showToast.warning("Please select a dataset first");
      return;
    }

    if (!newColumnKey.trim()) {
      showToast.warning(
        t("dataEntry.columnKeyRequired", "Column key is required"),
      );
      return;
    }

    if (!newColumnLabel.trim()) {
      showToast.warning(
        t("dataEntry.columnLabelRequired", "Column label is required"),
      );
      return;
    }

    // Block system keys for sales_records on the frontend before hitting the API
    if (
      isSalesRecords(activeDataset) &&
      SALES_RECORDS_SYSTEM_KEYS.has(newColumnKey.trim().toLowerCase())
    ) {
      showToast.error(
        `"${newColumnKey.trim()}" is a system column of Sales Records and cannot be added manually. ` +
        "Choose a different key for your custom column.",
      );
      return;
    }

    // Check for duplicate key
    if (columns.some((col) => col.key === newColumnKey.trim())) {
      showToast.error(
        t(
          "dataEntry.columnKeyDuplicate",
          "A column with this key already exists",
        ),
      );
      return;
    }

    try {
      setAddingColumn(true);

      const payload = {
        key: newColumnKey.trim(),
        label: newColumnLabel.trim(),
        column_type: newColumnType,
        required: false,
      };

      const response = await datasetsAPI.addColumn(
        organizationId,
        activeDataset.id,
        payload,
      );

      const newColumn = response?.data || response;

      showToast.success(
        t("dataEntry.columnAdded", "Column added successfully!"),
      );

      // Update columns list
      setColumns([
        ...columns,
        {
          id: newColumn.id,
          key: newColumn.key,
          label: newColumn.label,
          type: newColumn.column_type || "text",
        },
      ]);

      // Reset form and close modal
      setNewColumnKey("");
      setNewColumnLabel("");
      setNewColumnType("text");
      setShowAddColumnModal(false);

      // Reload dataset to get updated columns
      const updatedDataset = await datasetsAPI.get(
        organizationId,
        activeDataset.id,
      );
      setActiveDataset(updatedDataset.data || updatedDataset);
    } catch (error) {
      console.error("Failed to add column:", error);
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to add column";
      showToast.error(errorMsg);
    } finally {
      setAddingColumn(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <div className="card">
          <p>{t("dataEntry.loadingDatasets", "Loading datasets...")}</p>
        </div>
      </div>
    );
  }

  if (!organizationId) {
    return (
      <div className="page-content">
        <div className="card">
          <p>
            {t(
              "dataEntry.noOrganization",
              "No organization assigned. Please contact your administrator.",
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="card data-entry-card">
        <div className="tabs">
          {datasets.map((dataset) => (
            <button
              key={dataset.id}
              className={`tab ${activeDataset?.id === dataset.id ? "active" : ""}`}
              onClick={() => selectDataset(dataset)}
            >
              {dataset.label || dataset.name}
            </button>
          ))}
          <button
            className="tab tab-add"
            onClick={() => {
              setNewDatasetName("");
              setNewDatasetLabel("");
              setColumns([
                {
                  id: 1,
                  key: "column1",
                  label: t("dataEntry.column1Default", "Column 1"),
                  type: "text",
                },
              ]);
              setShowAddDatasetModal(true);
            }}
          >
            + {t("dataEntry.addCustom", "Add Custom")}
          </button>
        </div>

        {activeDataset ? (
          <>
            {/* Mode Tabs */}
            <div
              style={{
                display: "flex",
                gap: "8px",
                padding: "12px 16px 0 16px",
                borderBottom: "1px solid var(--border-grey)",
                marginBottom: "16px",
              }}
            >
              <button
                className={`btn btn-sm ${viewMode === "entry" ? "btn-primary" : "btn-secondary"}`}
                onClick={() => setViewMode("entry")}
                style={{
                  minWidth: "120px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  justifyContent: "center",
                }}
              >
                <FiEdit /> {t("dataEntry.newEntry")}
              </button>
              <button
                className={`btn btn-sm ${viewMode === "view" ? "btn-primary" : "btn-secondary"}`}
                onClick={() => {
                  setViewMode("view");
                  if (currentRowsPage !== 1) {
                    setCurrentRowsPage(1);
                    if (activeDataset) {
                      loadExistingRows(activeDataset.id, 1, rowsPageSize);
                    }
                  }
                }}
                style={{
                  minWidth: "120px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  justifyContent: "center",
                }}
              >
                <FiEye /> {t("dataEntry.viewData")}{" "}
                {rowsPagination
                  ? `(${rowsPagination.total_items})`
                  : existingRows.length > 0
                    ? `(${existingRows.length})`
                    : ""}
              </button>
              <div
                style={{
                  marginLeft: "auto",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexWrap: "wrap",
                  position: "relative",
                }}
              >
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowExportPanel(!showExportPanel)}
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  <FiDownload /> {t("dataEntry.exportCsv", "Export CSV")}
                </button>
                {showExportPanel && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      right: 0,
                      marginTop: "8px",
                      background: "var(--bg-white, #fff)",
                      border: "1px solid var(--border-grey)",
                      borderRadius: "10px",
                      padding: "16px",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                      zIndex: 20,
                      minWidth: "280px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        marginBottom: "4px",
                      }}
                    >
                      {t("dataEntry.export", "Export")}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                      }}
                    >
                      <label
                        style={{ fontSize: "12px", color: "var(--text-grey)" }}
                      >
                        From
                      </label>
                      <input
                        type="date"
                        value={exportDateFrom}
                        onChange={(e) => setExportDateFrom(e.target.value)}
                        style={{
                          padding: "6px 8px",
                          borderRadius: "6px",
                          border: "1px solid var(--border-grey)",
                          width: "100%",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                      }}
                    >
                      <label
                        style={{ fontSize: "12px", color: "var(--text-grey)" }}
                      >
                        To
                      </label>
                      <input
                        type="date"
                        value={exportDateTo}
                        onChange={(e) => setExportDateTo(e.target.value)}
                        style={{
                          padding: "6px 8px",
                          borderRadius: "6px",
                          border: "1px solid var(--border-grey)",
                          width: "100%",
                        }}
                      />
                    </div>
                    <select
                      value={exportDatasetId || activeDataset?.id || ""}
                      onChange={(e) => setExportDatasetId(e.target.value)}
                      style={{
                        padding: "6px 8px",
                        borderRadius: "6px",
                        border: "1px solid var(--border-grey)",
                        width: "100%",
                      }}
                    >
                      <option value="">Select dataset</option>
                      {datasets.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.label || d.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        handleExportCsv();
                        setShowExportPanel(false);
                      }}
                      disabled={exporting || !exportDateFrom || !exportDateTo}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        justifyContent: "center",
                        width: "100%",
                      }}
                    >
                      <FiDownload />{" "}
                      {exporting
                        ? t("dataEntry.exporting", "Exporting...")
                        : t("dataEntry.exportCsv", "Export CSV")}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {viewMode === "entry" ? (
              // Entry Mode - Add new rows
              <>
                {/* ── Managed-dataset notice for sales_records ── */}
                {isSalesRecords(activeDataset) && (
                  <div style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                    margin: "0 0 16px 0",
                    padding: "12px 16px",
                    borderRadius: "8px",
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    fontSize: "13px",
                    color: "#1e40af",
                  }}>
                    <FiLock style={{ flexShrink: 0, marginTop: 2 }} />
                    <div>
                      <strong>Sales Records is auto-managed.</strong>{" "}
                      Rows are created automatically when you record a sale in the{" "}
                      <strong>Sales</strong> module. Manual row entry and CSV upload
                      are disabled here to prevent data mismatches.
                      <br />
                      You can still <strong>add custom columns</strong> (non-sales
                      fields) in the View Data tab and edit their values per row.
                    </div>
                  </div>
                )}

                <div className="data-entry-layout">
                  <div className="data-entry-modes">
                    <div className="mode-card" style={isSalesRecords(activeDataset) ? { opacity: 0.45, pointerEvents: "none" } : {}}>
                      <div className="mode-header">
                        <div className="mode-icon">
                          <FiEdit />
                        </div>
                        <div>
                          <div className="mode-title">
                            {t(
                              "dataEntry.addRowsManually",
                              "Add Rows Manually",
                            )}
                          </div>
                          <div className="mode-subtitle">
                            {isSalesRecords(activeDataset)
                              ? "Disabled — rows come from Sales"
                              : t(
                                  "dataEntry.addRowsManuallySub",
                                  "Best for a few records or quick corrections.",
                                )}
                          </div>
                        </div>
                      </div>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={addRow}
                        disabled={isSalesRecords(activeDataset)}
                      >
                        <FiPlus /> {t("dataEntry.addRow", "Add Row")}
                      </button>
                    </div>

                    <div className="mode-card" style={isSalesRecords(activeDataset) ? { opacity: 0.45, pointerEvents: "none" } : {}}>
                      <div className="mode-header">
                        <div className="mode-icon">
                          <FiUpload />
                        </div>
                        <div>
                          <div className="mode-title">
                            {t("dataEntry.uploadFromCsv", "Upload from CSV")}
                          </div>
                          <div className="mode-subtitle">
                            {isSalesRecords(activeDataset)
                              ? "Disabled — rows come from Sales"
                              : t(
                                  "dataEntry.uploadFromCsvSub",
                                  "Use the template to prepare many rows at once.",
                                )}
                          </div>
                        </div>
                      </div>
                      <div className="mode-actions">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={downloadTemplate}
                          title="Download CSV template with column headers"
                          disabled={isSalesRecords(activeDataset)}
                        >
                          <FiDownload />{" "}
                          {t("dataEntry.downloadTemplate", "Download Template")}
                        </button>
                        <label
                          className="btn btn-secondary btn-sm"
                          style={{
                            cursor: uploadingBulk || isSalesRecords(activeDataset) ? "not-allowed" : "pointer",
                            opacity: uploadingBulk || isSalesRecords(activeDataset) ? 0.7 : 1,
                          }}
                        >
                          <FiUpload />{" "}
                          {uploadingBulk
                            ? t("dataEntry.uploading", "Uploading...")
                            : t("dataEntry.uploadCsv", "Upload CSV")}
                          <input
                            ref={bulkInputRef}
                            type="file"
                            accept=".csv"
                            style={{ display: "none" }}
                            onChange={handleBulkUpload}
                            disabled={uploadingBulk || isSalesRecords(activeDataset)}
                          />
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="data-entry-summary">
                    <div className="summary-text">
                      <div className="summary-label">
                        {t("dataEntry.activeDataset", "Active dataset")}
                      </div>
                      <div className="summary-value">
                        {activeDataset.label || activeDataset.name}
                      </div>
                      <div className="summary-meta">
                        {columns.length}{" "}
                        {t("dataEntry.columnsDefined", "columns defined")}
                      </div>
                    </div>
                    <button
                      className="btn btn-success btn-sm"
                      onClick={submitData}
                      disabled={
                        submitting || rows.length === 0 || columns.length === 0
                      }
                    >
                      {submitting
                        ? t("dataEntry.submitting", "Submitting...")
                        : t("dataEntry.submitData", "Submit Data")}
                    </button>
                  </div>
                </div>

                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        {columns.map((col) => (
                          <th key={col.id}>
                            <div className="th-content">
                              <div className="th-label" title={col.key}>
                                {col.label}
                              </div>
                              <div className="th-type">
                                {t(
                                  `columnType.${col.type}`,
                                  col.type?.toUpperCase(),
                                )}
                              </div>
                            </div>
                          </th>
                        ))}
                        <th style={{ width: "80px" }}>
                          {t("dataEntry.actions", "Actions")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length > 0 ? (
                        rows.map((row) => (
                          <tr key={row.id}>
                            {columns.map((col) => (
                              <td key={col.id}>
                                <input
                                  type={col.type}
                                  value={row[col.key] || ""}
                                  onChange={(e) =>
                                    updateCell(row.id, col.key, e.target.value)
                                  }
                                  className="td-input"
                                />
                              </td>
                            ))}
                            <td>
                              <button
                                className="btn-delete"
                                onClick={() => deleteRow(row.id)}
                                title={t("dataEntry.deleteRow", "Delete row")}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <FiTrash2 />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={columns.length + 1}
                            style={{
                              textAlign: "center",
                              padding: "40px",
                              color: "var(--text-grey)",
                            }}
                          >
                            {t(
                              "dataEntry.noRows",
                              'No data rows. Click "+ Add Row" to start adding data.',
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              // View Mode - Show existing rows with edit/delete
              <>
                <div className="data-entry-toolbar">
                  <div className="toolbar-left">
                    <span
                      style={{
                        fontSize: "13px",
                        color: "var(--text-grey)",
                      }}
                    >
                      {t(
                        "dataEntry.datasetSummary",
                        "Dataset: {{name}} | {{rows}} row(s) | {{columns}} columns",
                        {
                          name: activeDataset.label || activeDataset.name,
                          rows: existingRows.length,
                          columns: columns.length,
                        },
                      )}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => setShowAddColumnModal(true)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <FiPlus />
                      {t("dataEntry.addColumn", "Add Column")}
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => loadExistingRows(activeDataset.id)}
                      disabled={loadingRows}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <FiRefreshCw className={loadingRows ? "rotating" : ""} />
                      {loadingRows
                        ? t("dataEntry.loadingData", "Loading data...")
                        : t("common.refresh", "Refresh")}
                    </button>
                  </div>
                </div>

                {loadingRows ? (
                  <div style={{ padding: "40px", textAlign: "center" }}>
                    <p>{t("dataEntry.loadingData", "Loading data...")}</p>
                  </div>
                ) : existingRows && existingRows.length > 0 ? (
                  <>
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            {columns.map((col) => {
                              const isSystemCol =
                                isSalesRecords(activeDataset) &&
                                SALES_RECORDS_SYSTEM_KEYS.has(col.key);
                              return (
                              <th key={col.id}>
                                <div className="th-content">
                                  <div className="th-label" title={col.key}
                                    style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                    {isSystemCol && (
                                      <FiLock style={{ fontSize: 10, opacity: 0.5, flexShrink: 0 }} />
                                    )}
                                    {col.label}
                                  </div>
                                  <div className="th-type">
                                    {t(
                                      `columnType.${col.type}`,
                                      col.type?.toUpperCase(),
                                    )}
                                  </div>
                                </div>
                              </th>
                              );
                            })}
                            <th style={{ width: "120px" }}>
                              {t("dataEntry.actions", "Actions")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {existingRows.map((row) => {
                            const isEditing = editingRowIds.has(row.id);
                            return (
                              <tr key={row.id}>
                                {columns.map((col) => {
                                  // System columns in sales_records are always read-only
                                  const isSystemCol =
                                    isSalesRecords(activeDataset) &&
                                    SALES_RECORDS_SYSTEM_KEYS.has(col.key);
                                  return (
                                  <td key={col.id}>
                                    {isEditing && !isSystemCol ? (
                                      <input
                                        type={col.type}
                                        value={row.data?.[col.key] || ""}
                                        onChange={(e) =>
                                          updateExistingCell(
                                            row.id,
                                            col.key,
                                            e.target.value,
                                          )
                                        }
                                        className="td-input"
                                      />
                                    ) : (
                                      <div style={{
                                        padding: "8px",
                                        color: isSystemCol ? "var(--text-grey)" : undefined,
                                        fontStyle: isSystemCol ? "italic" : undefined,
                                      }}>
                                        {row.data?.[col.key] || "-"}
                                      </div>
                                    )}
                                  </td>
                                  );
                                })}
                                <td>
                                  <div style={{ display: "flex", gap: "4px" }}>
                                    {isEditing ? (
                                      <>
                                        <button
                                          className="btn-save"
                                          onClick={() =>
                                            updateExistingRow(row.id, row)
                                          }
                                          title="Save changes"
                                          style={{
                                            background: "#10b981",
                                            color: "white",
                                            border: "none",
                                            padding: "6px 10px",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            fontSize: "14px",
                                            display: "flex",
                                            alignItems: "center",
                                          }}
                                        >
                                          <FiSave />
                                        </button>
                                        <button
                                          className="btn-cancel"
                                          onClick={() => toggleEditMode(row.id)}
                                          title="Cancel"
                                          style={{
                                            background: "#6b7280",
                                            color: "white",
                                            border: "none",
                                            padding: "6px 10px",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            fontSize: "14px",
                                            display: "flex",
                                            alignItems: "center",
                                          }}
                                        >
                                          <FiX />
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          className="btn-edit"
                                          onClick={() => toggleEditMode(row.id)}
                                          title="Edit row"
                                          style={{
                                            background: "#3b82f6",
                                            color: "white",
                                            border: "none",
                                            padding: "6px 10px",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            fontSize: "14px",
                                            display: "flex",
                                            alignItems: "center",
                                          }}
                                        >
                                          <FiEdit2 />
                                        </button>
                                        <button
                                          className="btn-delete"
                                          onClick={() =>
                                            deleteExistingRow(row.id)
                                          }
                                          title="Delete row"
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            padding: "6px 10px",
                                            fontSize: "14px",
                                          }}
                                        >
                                          <FiTrash2 />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination Controls */}
                    {rowsPagination && rowsPagination.total_pages > 1 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginTop: "16px",
                          padding: "12px",
                          background: "var(--bg-grey)",
                          borderRadius: "6px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "13px",
                            color: "var(--text-grey)",
                          }}
                        >
                          {t(
                            "dataEntry.paginationSummary",
                            "Showing {{shown}} of {{total}} rows (Page {{current}} of {{pages}})",
                            {
                              shown: existingRows.length,
                              total: rowsPagination.total_items,
                              current: rowsPagination.current_page,
                              pages: rowsPagination.total_pages,
                            },
                          )}
                        </div>
                        <div style={{ display: "flex", gap: "8px" }}>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => {
                              const newPage = currentRowsPage - 1;
                              setCurrentRowsPage(newPage);
                              loadExistingRows(
                                activeDataset.id,
                                newPage,
                                rowsPageSize,
                              );
                            }}
                            disabled={!rowsPagination.has_previous}
                            style={{
                              opacity: rowsPagination.has_previous ? 1 : 0.5,
                              cursor: rowsPagination.has_previous
                                ? "pointer"
                                : "not-allowed",
                            }}
                          >
                            {t("dataEntry.previous", "« Previous")}
                          </button>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => {
                              const newPage = currentRowsPage + 1;
                              setCurrentRowsPage(newPage);
                              loadExistingRows(
                                activeDataset.id,
                                newPage,
                                rowsPageSize,
                              );
                            }}
                            disabled={!rowsPagination.has_next}
                            style={{
                              opacity: rowsPagination.has_next ? 1 : 0.5,
                              cursor: rowsPagination.has_next
                                ? "pointer"
                                : "not-allowed",
                            }}
                          >
                            {t("dataEntry.next", "Next »")}
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    style={{
                      padding: "40px",
                      textAlign: "center",
                      color: "var(--text-grey)",
                    }}
                  >
                    <p>
                      {t(
                        "dataEntry.noExistingRows",
                        'No data rows found. Switch to "New Entry" mode to add data.',
                      )}
                    </p>
                  </div>
                )}
              </>
            )}
          </>
        ) : datasets.length === 0 ? (
          <div style={{ padding: "40px", textAlign: "center" }}>
            <p style={{ color: "var(--text-grey)", marginBottom: "16px" }}>
              No datasets found. Create your first dataset to start entering
              data.
            </p>
            <button
              className="btn btn-primary"
              onClick={() => setShowAddDatasetModal(true)}
            >
              + Create Dataset
            </button>
          </div>
        ) : (
          <div
            style={{
              padding: "40px",
              textAlign: "center",
              color: "var(--text-grey)",
            }}
          >
            Select a dataset from the tabs above to start entering data.
          </div>
        )}
      </div>

      {/* Add Dataset Modal */}
      {showAddDatasetModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowAddDatasetModal(false)}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "660px" }}
          >
            <div className="modal-header">
              <h3>{t("dataEntry.createDatasetTitle", "Create New Dataset")}</h3>
              <button
                className="modal-close"
                onClick={() => setShowAddDatasetModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t("dataEntry.datasetName", "Dataset Name*")}</label>
                <input
                  type="text"
                  value={newDatasetName}
                  onChange={(e) => setNewDatasetName(e.target.value)}
                  placeholder="e.g., sales_performance_q1"
                  autoFocus
                />
                <small style={{ fontSize: "12px", color: "var(--text-grey)" }}>
                  Use lowercase with underscores (will be auto-formatted)
                </small>
              </div>
              <div className="form-group">
                <label>
                  {t(
                    "dataEntry.datasetLabelOptional",
                    "Display Label (optional)",
                  )}
                </label>
                <input
                  type="text"
                  value={newDatasetLabel}
                  onChange={(e) => setNewDatasetLabel(e.target.value)}
                  placeholder="e.g., Sales Performance Q1"
                />
              </div>

              <hr
                style={{
                  margin: "24px 0",
                  border: "none",
                  borderTop: "1px solid var(--border-grey)",
                }}
              />

              <div className="form-group">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "12px",
                  }}
                >
                  <label style={{ margin: 0 }}>
                    {t("dataEntry.columnDefinitions", "Column Definitions*")} (
                    {columns.length})
                  </label>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      const newCol = {
                        id: Date.now(),
                        key: `column_${columns.length + 1}`,
                        label: `Column ${columns.length + 1}`,
                        type: "text",
                      };
                      setColumns([...columns, newCol]);
                    }}
                  >
                    + {t("dataEntry.addColumn", "Add Column")}
                  </button>
                </div>

                {columns.length > 0 ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    {columns.map((col, idx) => (
                      <div
                        key={col.id}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr 100px 40px",
                          gap: "8px",
                          alignItems: "center",
                          padding: "8px",
                          background: "var(--bg-grey)",
                          borderRadius: "6px",
                        }}
                      >
                        <input
                          type="text"
                          value={col.key}
                          onChange={(e) => {
                            const updated = [...columns];
                            updated[idx] = { ...col, key: e.target.value };
                            setColumns(updated);
                          }}
                          placeholder="key"
                          style={{ padding: "8px", fontSize: "13px" }}
                        />
                        <input
                          type="text"
                          value={col.label}
                          onChange={(e) => {
                            const updated = [...columns];
                            updated[idx] = { ...col, label: e.target.value };
                            setColumns(updated);
                          }}
                          placeholder="Label"
                          style={{ padding: "8px", fontSize: "13px" }}
                        />
                        <select
                          value={col.type}
                          onChange={(e) => {
                            const updated = [...columns];
                            updated[idx] = { ...col, type: e.target.value };
                            setColumns(updated);
                          }}
                          style={{ padding: "8px", fontSize: "13px" }}
                        >
                          <option value="text">Text</option>
                          <option value="number">Number</option>
                          <option value="date">Date</option>
                          <option value="boolean">Boolean</option>
                        </select>
                        <button
                          type="button"
                          onClick={() => {
                            setColumns(columns.filter((c) => c.id !== col.id));
                          }}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            fontSize: "18px",
                            opacity: 0.6,
                          }}
                          title="Remove column"
                        >
                          Delete
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      padding: "24px",
                      textAlign: "center",
                      background: "var(--bg-grey)",
                      borderRadius: "6px",
                      color: "var(--text-grey)",
                    }}
                  >
                    {t(
                      "dataEntry.noColumnsPrompt",
                      'No columns defined. Click "+ Add Column" to start.',
                    )}
                  </div>
                )}
              </div>

              <div
                style={{
                  marginTop: "16px",
                  padding: "12px",
                  background: "#EFF6FF",
                  borderRadius: "6px",
                  fontSize: "13px",
                  color: "#1E40AF",
                }}
              >
                ℹ️{" "}
                {t(
                  "dataEntry.structureCannotChange",
                  "Column structure cannot be changed after dataset creation. Make sure to define all columns now.",
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowAddDatasetModal(false)}
              >
                {t("dataEntry.cancel", "Cancel")}
              </button>
              <button
                className="btn btn-primary"
                onClick={createNewDataset}
                disabled={!newDatasetName.trim() || columns.length === 0}
              >
                {t("dataEntry.createDatasetButton", "Create Dataset")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Column Modal */}
      {showAddColumnModal && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowAddColumnModal(false);
            setNewColumnKey("");
            setNewColumnLabel("");
            setNewColumnType("text");
          }}
        >
          <div
            className="modal-content"
            style={{ maxWidth: "460px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>{t("dataEntry.addColumnTitle", "Add New Column")}</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowAddColumnModal(false);
                  setNewColumnKey("");
                  setNewColumnLabel("");
                  setNewColumnType("text");
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {/* Hint for sales_records managed columns */}
              {isSalesRecords(activeDataset) && (
                <div style={{
                  display: "flex", gap: 8, alignItems: "flex-start",
                  padding: "10px 12px", borderRadius: 6, marginBottom: 16,
                  background: "#eff6ff", border: "1px solid #bfdbfe",
                  fontSize: 12, color: "#1e40af",
                }}>
                  <FiLock style={{ flexShrink: 0, marginTop: 2 }} />
                  <span>
                    You are adding a <strong>custom column</strong> to Sales Records.
                    The following keys are reserved and cannot be used:{" "}
                    <em>{[...SALES_RECORDS_SYSTEM_KEYS].join(", ")}</em>.
                  </span>
                </div>
              )}

              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", marginBottom: "8px" }}>
                  {t("dataEntry.columnKey", "Column Key")}{" "}
                  <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="text"
                  className="input"
                  placeholder="e.g., employee_id"
                  value={newColumnKey}
                  onChange={(e) => setNewColumnKey(e.target.value)}
                  style={{
                    width: "100%",
                    borderColor:
                      isSalesRecords(activeDataset) &&
                      SALES_RECORDS_SYSTEM_KEYS.has(newColumnKey.trim().toLowerCase())
                        ? "#ef4444"
                        : undefined,
                  }}
                />
                {isSalesRecords(activeDataset) &&
                  SALES_RECORDS_SYSTEM_KEYS.has(newColumnKey.trim().toLowerCase()) && (
                  <small style={{ color: "#ef4444", fontSize: "12px" }}>
                    This key is reserved. Choose a different name.
                  </small>
                )}
                {!(isSalesRecords(activeDataset) &&
                  SALES_RECORDS_SYSTEM_KEYS.has(newColumnKey.trim().toLowerCase())) && (
                  <small style={{ color: "var(--text-grey)", fontSize: "12px" }}>
                    {t(
                      "dataEntry.columnKeyHelp",
                      "Unique identifier for this column (no spaces)",
                    )}
                  </small>
                )}
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", marginBottom: "8px" }}>
                  {t("dataEntry.columnLabel", "Column Label")}{" "}
                  <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="text"
                  className="input"
                  placeholder="e.g., Employee ID"
                  value={newColumnLabel}
                  onChange={(e) => setNewColumnLabel(e.target.value)}
                  style={{ width: "100%" }}
                />
                <small style={{ color: "var(--text-grey)", fontSize: "12px" }}>
                  {t(
                    "dataEntry.columnLabelHelp",
                    "Display name for this column",
                  )}
                </small>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", marginBottom: "8px" }}>
                  {t("dataEntry.columnType", "Column Type")}
                </label>
                <select
                  className="input"
                  value={newColumnType}
                  onChange={(e) => setNewColumnType(e.target.value)}
                  style={{ width: "100%" }}
                >
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="date">Date</option>
                  <option value="email">Email</option>
                  <option value="url">URL</option>
                </select>
              </div>

              <div
                style={{
                  padding: "12px",
                  background: "#FEF3C7",
                  borderRadius: "6px",
                  fontSize: "13px",
                  color: "#92400E",
                }}
              >
                !{" "}
                {t(
                  "dataEntry.existingRowsInfo",
                  "Existing rows will have empty values for this new column.",
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowAddColumnModal(false);
                  setNewColumnKey("");
                  setNewColumnLabel("");
                  setNewColumnType("text");
                }}
                disabled={addingColumn}
              >
                {t("dataEntry.cancel", "Cancel")}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddColumn}
                disabled={
                  !newColumnKey.trim() || !newColumnLabel.trim() || addingColumn
                }
              >
                {addingColumn
                  ? t("dataEntry.addingColumn", "Adding...")
                  : t("dataEntry.addColumnButton", "Add Column")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataEntry;
